import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Button,
  FormGroup,
  Label,
  Container,
} from "reactstrap";
import { useNavigate, Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import Select from "react-select";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import PrintableTable from "./Components/PrintableTable";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import NumberCommas from "../../Components/NumberCommas/NumberCommas";
import PrintHeader from "../../Components/PrintHeader/PrintHeader";
import { formatDate } from "../../Components/FormatDate/FormatDate";
import SupplierPurchasedServices from "../../Services/QRCodeServices/SupplierPurchasedServices/SupplierPurchasedServices";
import SupplierServices from "../../Services/QRCodeServices/SupplierServices/SupplierServices";

const SupplierPurchased = () => {
  const navigate = useNavigate();
  const [supplierDetail, setSupplierDetail] = useState([]);
  const [supplierID, setSupplierID] = useState([]);
  const [dataS, setDataS] = useState("");
  const [loading, setLoading] = useState(false);
  const [isReportGenerated, setIsReportGenerated] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [selectedSupplierDetails, setSelectedSupplierDetails] = useState(null);

  const options = supplierID.map((supplier) => ({
    value: supplier.id,
    label: supplier.supplierName,
  }));

  const handleChange = (selectedOption) => {
    const selectedSupplier = supplierID.find(
      (supplier) => supplier.id === selectedOption.value
    );
    setDataS(selectedOption ? selectedOption.value : "");
    setSelectedSupplierDetails(selectedSupplier);
    setIsReportGenerated(false);
    setSupplierDetail([]);
  };

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
      },
    },
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (row.id === "total-row" ? "" : index + 1),
      sortable: true,
      width: "80px",
    },
    {
      name: "Transaction Date",
      selector: (row) => row.valueData,
      sortable: true,
      width: "200px",
      cell: (row) => (
        <div className={row.id === "total-row" ? "fw-bold" : ""}>
          {formatDate(row.valueData)}
        </div>
      ),
    },
    {
      name: "Invoice Number",
      selector: (row) => row.invoiceNumber,
      cell: (row) => (
        <Link to={`/purchases/details/${row.id}`}>{row.invoiceNumber}</Link>
      ),
      sortable: true,
    },
    {
      name: "Sub Total",
      selector: (row) => row.subTotal,
      sortable: true,
      width: "150px",
      cell: (row) => <NumberCommas number={row.subTotal} />,
      right: true,
    },
    {
      name: "Total",
      selector: (row) => row.total,
      sortable: true,
      width: "150px",
      cell: (row) => <NumberCommas number={row.total} />,
      right: true,
    },
    // {
    //   name: "Net Amount",
    //   selector: (row) => row.netAmt,
    //   sortable: true,
    //   width: "150px",
    //   cell: (row) => (
    //     <div
    //       className={`text-end w-100 ${
    //         row.id === "total-row" ? "fw-bold" : ""
    //       }`}
    //     >
    //       <NumberCommas number={row.netAmt} />
    //     </div>
    //   ),
    // },
    {
      name: "Remarks",
      selector: (row) => row.remarks,
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) =>
        row.id === "total-row" ? null : (
          <Link to={`/purchases/details/${row.id}`}>
            <Button color="btn btn-soft-success" className="btn-sm gap-1">
              <i className="bx bx-show" />
            </Button>
          </Link>
        ),
      omit: isPrinting,
    },
  ];

  async function fetchPurchasePosts(supplier_id) {
    try {
      setLoading(true);
      const response = await SupplierPurchasedServices.getList(supplier_id);
      setSupplierDetail(response);
      setIsReportGenerated(true);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching purchase details:", error);
      toast.error(error.response?.data || "Error fetching purchase details");
      setLoading(false);
      setIsReportGenerated(false);
    }
  }

  async function fetchSuppliers() {
    try {
      const fetchedSuppliers = await SupplierServices.getList();
      setSupplierID(fetchedSuppliers?.data);
    } catch (error) {
      toast.error(error.response?.data || "Error fetching suppliers");
    }
  }

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const generateReport = () => {
    fetchPurchasePosts(dataS);
  };

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 500);
  };

  const totalSubTotal = supplierDetail.reduce(
    (sum, row) => sum + row.subTotal,
    0
  );
  const totalTotal = supplierDetail.reduce((sum, row) => sum + row.total, 0);
  const totalNetAmt = supplierDetail.reduce((sum, row) => sum + row.netAmt, 0);

  const totalRow = {
    valueData: "Total",
    invoiceNumber: "",
    subTotal: totalSubTotal,
    total: totalTotal,
    netAmt: totalNetAmt,
    remarks: "",
    id: "total-row",
  };

  const tableData =
    supplierDetail.length > 0 ? [...supplierDetail, totalRow] : supplierDetail;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Supplier Performance Report" pageTitle="Supplier Performance" />
        <Row>
          <Col>
            <Card>
              <CardBody>
                <PrintHeader reportTitle="Supplier Purchase Report" />
                <Row className="d-print-none">
                  <FormGroup row>
                    <Col sm={2}>
                      <Label for="supplierID">Suppliers:</Label>
                    </Col>
                    <Col sm={4}>
                      <Select
                        value={options.find((option) => option.value === dataS)}
                        onChange={handleChange}
                        options={options}
                      />
                    </Col>
                    <Col sm={3}>
                      <Button
                        color="primary"
                        onClick={generateReport}
                        disabled={!dataS}
                      >
                        Generate Report
                      </Button>
                    </Col>
                    <Col sm={3} className="text-end">
                      {isReportGenerated && (
                        <Button
                          color="outline-secondary"
                          className="py-2 px-3 d-flex align-items-center shadow-sm ms-auto"
                          onClick={handlePrint}
                        >
                          <i className="ri-printer-line fs-16"></i>
                        </Button>
                      )}
                    </Col>
                  </FormGroup>
                </Row>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                {isPrinting ? (
                  <PrintableTable supplierDetail={supplierDetail} />
                ) : (
                  <DataTable
                    columns={columns}
                    data={tableData}
                    customStyles={customStyles}
                    pagination
                    responsive
                    striped
                    highlightOnHover
                    paginationPerPage={100}
                    paginationRowsPerPageOptions={[10, 25, 50, 100, 200, 500]}
                    progressPending={loading}
                    progressComponent={
                      <div className="my-3">
                        <Triangle
                          visible={true}
                          height="80"
                          width="80"
                          color="#5B71B9"
                          ariaLabel="triangle-loading"
                        />
                        <h5 className="mt-1">Loading...</h5>
                      </div>
                    }
                    noDataComponent={
                      <div className="text-center p-4">
                        <h5>No data available</h5>
                      </div>
                    }
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SupplierPurchased;
